/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
    q,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
        q,
      },
    });
  }

  inboxSearch({ q, inboxId, teamId, page = 1 }) {
    return axios.get(`${this.url}/inbox_search`, {
      params: {
        q,
        inbox_id: inboxId,
        team_id: teamId || undefined,
        page,
        status: 'all',
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
      },
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({
    conversationId,
    status,
    snoozedUntil = null,
    serviceSuspended = undefined,
  }) {
    const payload = {};
    if (status !== undefined && status !== null) payload.status = status;
    if (snoozedUntil) payload.snoozed_until = snoozedUntil;
    if (serviceSuspended !== undefined) {
      payload.service_suspended = serviceSuspended;
    }
    return axios.post(`${this.url}/${conversationId}/toggle_status`, payload);
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(`${this.url}/${conversationId}/assignments`, {
      assignee_id: agentId,
    });
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  silence(conversationId) {
    return axios.post(`${this.url}/${conversationId}/silence`);
  }

  unsilence(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unsilence`);
  }

  getScheduledMessage(conversationId) {
    return axios.get(`${this.url}/${conversationId}/scheduled_messages`);
  }

  createScheduledMessage(conversationId, data) {
    return axios.post(`${this.url}/${conversationId}/scheduled_messages`, {
      scheduled_message: data,
    });
  }

  cancelScheduledMessage(conversationId) {
    return axios.delete(`${this.url}/${conversationId}/scheduled_messages`);
  }

  meta({
    inboxId,
    status,
    assigneeType,
    labels,
    teamId,
    conversationType,
    sidebarStats,
  }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
        sidebar_stats: sidebarStats,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  favorite(conversationId) {
    return axios.post(`${this.url}/${conversationId}/favorite`);
  }

  unfavorite(conversationId) {
    return axios.delete(`${this.url}/${conversationId}/unfavorite`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
